import React, { useState, useRef, useEffect, useContext } from "react";
import { AppContext } from "../context";
import Navbar from "./Navbar";

/**
 * Phone verification by SMS one-time code.
 *
 * A React port of the shared otp-verification.html, wired to the backend
 * instead of the mock: POST /sendOtp texts a 6-digit code, POST /verifyOtp
 * checks it. The code is generated and held server-side, so nothing here can
 * tell you what it is.
 *
 * Renders the phone step, then reveals the code step in place. A correct code
 * calls onVerified() immediately -- no confirmation panel, the parent moves the
 * user straight on.
 */

const RESEND_SECONDS = 30;
const CODE_LENGTH = 6;

const COUNTRY_CODES = [
  { code: "+91", label: "🇮🇳 +91" },
  { code: "+1", label: "🇺🇸 +1" },
  { code: "+44", label: "🇬🇧 +44" },
  { code: "+971", label: "🇦🇪 +971" },
  { code: "+65", label: "🇸🇬 +65" },
  { code: "+61", label: "🇦🇺 +61" },
  { code: "+49", label: "🇩🇪 +49" },
  { code: "+33", label: "🇫🇷 +33" },
  { code: "+81", label: "🇯🇵 +81" },
  { code: "+86", label: "🇨🇳 +86" },
  { code: "+974", label: "🇶🇦 +974" },
  { code: "+966", label: "🇸🇦 +966" },
];

// Server reasons -> what the user should read. Anything unmapped falls back to
// a generic line rather than surfacing a raw reason code.
const SEND_ERRORS = {
  invalid_phone: "That number doesn't look right. Please check and try again.",
  not_configured: "SMS is not set up yet. Please contact support.",
  send_failed: "We couldn't send the code. Please try again in a moment.",
  ip_rate_limited: "Too many requests from your network. Please try again later.",
  server_error: "Something went wrong. Please try again.",
};

const VERIFY_ERRORS = {
  wrong_code: "That code doesn't look right. Please try again.",
  invalid_code: "Please enter all 6 digits.",
  expired: "That code has expired. Please request a new one.",
  too_many_attempts: "Too many wrong attempts. Please request a new code.",
  ip_rate_limited: "Too many attempts from your network. Please try again later.",
  server_error: "Something went wrong. Please try again.",
};

// Same primary colour every other button in the app uses. The navy is only a
// fallback for a client config that carries no colours -- it is the shade the
// page was designed in.
const DEFAULT_ACCENT = "#1c1550";

const OtpVerification = ({ onVerified }) => {
  const { inspectionId, config } = useContext(AppContext);

  const accent =
    (config && config["colors"] && config["colors"]["btn"]) || DEFAULT_ACCENT;

  const [countryCode, setCountryCode] = useState("+91");
  const [phone, setPhone] = useState("");
  const [phoneLocked, setPhoneLocked] = useState(false);
  const [phoneError, setPhoneError] = useState("");

  const [digits, setDigits] = useState(Array(CODE_LENGTH).fill(""));
  const [codeError, setCodeError] = useState("");
  const [shake, setShake] = useState(false);

  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);

  const boxRefs = useRef([]);
  const timerRef = useRef(null);

  const phoneValid = phone.replace(/\D/g, "").length >= 7;
  const codeComplete = digits.every((d) => d !== "");

  // --- resend countdown ---------------------------------------------------
  const startTimer = (seconds = RESEND_SECONDS) => {
    if (timerRef.current) clearInterval(timerRef.current);
    setSecondsLeft(seconds);
    timerRef.current = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          clearInterval(timerRef.current);
          timerRef.current = null;
          return 0;
        }
        return s - 1;
      });
    }, 1000);
  };

  // Clear the interval on unmount so it cannot tick into a dead component.
  useEffect(
    () => () => {
      if (timerRef.current) clearInterval(timerRef.current);
    },
    []
  );

  const clearCode = () => {
    setDigits(Array(CODE_LENGTH).fill(""));
    setCodeError("");
  };

  // --- send / resend ------------------------------------------------------
  const requestCode = async () => {
    if (sending) return;
    setSending(true);
    setPhoneError("");
    try {
      const res = await fetch("/sendOtp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ countryCode, phoneNumber: phone }),
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        // A cooldown is not really an error: the previous code is still live, so
        // just show the timer again rather than blocking the user.
        if (data.reason === "cooldown") {
          setPhoneLocked(true);
          startTimer(data.retryAfterSeconds || RESEND_SECONDS);
          return;
        }
        setPhoneError(SEND_ERRORS[data.reason] || SEND_ERRORS.server_error);
        return;
      }

      setPhoneLocked(true);
      clearCode();
      startTimer(data.resendAfterSeconds || RESEND_SECONDS);
      // Focus the first box once it has rendered.
      setTimeout(() => boxRefs.current[0] && boxRefs.current[0].focus(), 0);
    } catch (e) {
      setPhoneError(SEND_ERRORS.server_error);
    } finally {
      setSending(false);
    }
  };

  const changeNumber = () => {
    setPhoneLocked(false);
    clearCode();
    if (timerRef.current) clearInterval(timerRef.current);
    setSecondsLeft(0);
  };

  // --- verify -------------------------------------------------------------
  const failCode = (message) => {
    setDigits(Array(CODE_LENGTH).fill(""));
    setCodeError(message);
    setShake(true);
    setTimeout(() => setShake(false), 400);
    if (boxRefs.current[0]) boxRefs.current[0].focus();
  };

  const submitCode = async () => {
    if (verifying || !codeComplete) return;
    setVerifying(true);
    try {
      const res = await fetch("/verifyOtp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          countryCode,
          phoneNumber: phone,
          code: digits.join(""),
          inspectionId,
        }),
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok || !data.verified) {
        let message = VERIFY_ERRORS[data.reason] || VERIFY_ERRORS.server_error;
        if (data.reason === "wrong_code" && data.attemptsLeft)
          message += ` ${data.attemptsLeft} attempt${
            data.attemptsLeft === 1 ? "" : "s"
          } left.`;
        failCode(message);
        return;
      }

      // Straight through to the next page: no confirmation panel, no delay. The
      // parent swaps this component out, so there is no success state to render.
      if (timerRef.current) clearInterval(timerRef.current);
      if (onVerified) onVerified();
    } catch (e) {
      failCode(VERIFY_ERRORS.server_error);
    } finally {
      setVerifying(false);
    }
  };

  // --- code box behaviour -------------------------------------------------
  const setDigitAt = (index, value) => {
    setDigits((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const onDigitInput = (index, raw) => {
    const value = raw.replace(/\D/g, "").slice(0, 1);
    setDigitAt(index, value);
    setCodeError("");
    const next = boxRefs.current[index + 1];
    if (value && index < CODE_LENGTH - 1 && next) next.focus();
  };

  const onDigitKeyDown = (index, e) => {
    const prev = boxRefs.current[index - 1];
    if (e.key === "Backspace" && !digits[index] && index > 0 && prev)
      prev.focus();
  };

  // Let the whole code be pasted, or arrive from an SMS autofill suggestion.
  const onDigitPaste = (e) => {
    e.preventDefault();
    const pasted = (e.clipboardData.getData("text").match(/\d/g) || []).slice(
      0,
      CODE_LENGTH
    );
    if (!pasted.length) return;
    const next = Array(CODE_LENGTH).fill("");
    pasted.forEach((d, i) => {
      next[i] = d;
    });
    setDigits(next);
    setCodeError("");
    const last = Math.min(pasted.length, CODE_LENGTH) - 1;
    if (boxRefs.current[last]) boxRefs.current[last].focus();
  };

  const showCodeStep = phoneLocked;

  // One variable drives the button, the focus rings and the links, so a client's
  // primary colour styles the whole page.
  return (
    <div className="otp-page" style={{ "--otp-accent": accent }}>
      {/* The app's own navbar, same as every other screen, so the client's logo
          and nav colour carry over instead of a second brand treatment here. */}
      <Navbar />
      <div className="otp-card">
        <div className="otp-content-wrap">
          <div className="otp-icon-badge">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="6" y="2" width="12" height="20" rx="2.5" />
              <path d="M11 18h2" />
            </svg>
          </div>

          <h1 className="otp-title">Verify your number</h1>
          <p className="otp-subtext">
            We&apos;ll text a {CODE_LENGTH}-digit code to confirm it&apos;s you.
          </p>

          {/* Phone entry */}
          <div className="otp-phone-section">
              <div className={`otp-phone-row ${phoneLocked ? "otp-locked" : ""}`}>
                <select
                  className="otp-country-code"
                  value={countryCode}
                  disabled={phoneLocked}
                  onChange={(e) => setCountryCode(e.target.value)}
                >
                  {COUNTRY_CODES.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.label}
                    </option>
                  ))}
                </select>
                <input
                  type="tel"
                  inputMode="numeric"
                  maxLength={12}
                  placeholder="Enter mobile number"
                  value={phone}
                  disabled={phoneLocked}
                  onChange={(e) => {
                    setPhone(e.target.value.replace(/\D/g, "").slice(0, 12));
                    setPhoneError("");
                  }}
                />
                {phoneLocked && (
                  <button className="otp-change-link" onClick={changeNumber}>
                    Change
                  </button>
                )}
              </div>
              <div className="otp-field-error">{phoneError}</div>
              {!showCodeStep && (
                <p className="otp-fine-print">
                  By continuing, you agree to receive a one-time SMS code for
                  verification. Message &amp; data rates may apply.
                </p>
              )}
          </div>

          {/* Code entry, revealed in place */}
          {showCodeStep && (
            <div className="otp-code-section">
              <p className="otp-subtext otp-code-subtext">
                Enter the {CODE_LENGTH}-digit code we just sent you
              </p>

              <div
                className={`otp-row ${codeError ? "otp-error-state" : ""} ${
                  shake ? "otp-shake" : ""
                }`}
              >
                {digits.map((d, i) => (
                  <input
                    key={i}
                    ref={(el) => {
                      boxRefs.current[i] = el;
                    }}
                    className={`otp-box ${d ? "otp-filled" : ""}`}
                    type="tel"
                    inputMode="numeric"
                    maxLength={1}
                    autoComplete={i === 0 ? "one-time-code" : "off"}
                    value={d}
                    onChange={(e) => onDigitInput(i, e.target.value)}
                    onKeyDown={(e) => onDigitKeyDown(i, e)}
                    onPaste={onDigitPaste}
                  />
                ))}
              </div>
              {codeError && <p className="otp-code-error">{codeError}</p>}
            </div>
          )}
        </div>

        {/* Pinned to the bottom of the card, outside the scrolling body, so the
            action sits in the same place on both steps and stays reachable when
            the keyboard is up. */}
        <div className="otp-footer">
          {showCodeStep ? (
            <>
              <button
                className={`otp-primary-btn ${
                  codeComplete && !verifying ? "otp-enabled" : ""
                }`}
                disabled={!codeComplete || verifying}
                onClick={submitCode}
              >
                {verifying ? "Verifying…" : "Verify & Continue"}
              </button>

              <div className="otp-resend-row">
                {secondsLeft > 0 ? (
                  <span>
                    Resend code in 0:
                    {String(secondsLeft).padStart(2, "0")}
                  </span>
                ) : (
                  <button
                    className="otp-resend-link"
                    disabled={sending}
                    onClick={() => requestCode(true)}
                  >
                    Resend OTP
                  </button>
                )}
              </div>
            </>
          ) : (
            <button
              className={`otp-primary-btn ${
                phoneValid && !sending ? "otp-enabled" : ""
              }`}
              disabled={!phoneValid || sending}
              onClick={() => requestCode(false)}
            >
              {sending ? "Sending…" : "Send OTP"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default OtpVerification;
